import {put, call, all} from 'redux-saga/effects';
import RedFlagType from "../../entities/redFlagType";
import * as Actions from "../actions/redFlagTypeActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type RedFlagTypeAction = {
    redFlagTypeId: string;
    redFlagType: RedFlagType;
    redFlagTypes: RedFlagType[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneRedFlagType(action: RedFlagTypeAction) {
	const session = tabSession.captureGeneration();
	try {
		const redFlagType: RedFlagType = yield call(api.findOne, 'redFlagType', action.redFlagTypeId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.REDFLAGTYPE_FETCH_ONE_SUCCESS, redFlagTypeId: action.redFlagTypeId, redFlagType, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REDFLAGTYPE_FETCH_ONE_FAILURE, redFlagTypeId: action.redFlagTypeId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {RedFlagTypeAction} action
 */
export function* saveRedFlagType(action: RedFlagTypeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ REDFLAGTYPE_SAVE saga', action.redFlagType?.id);

		if (action.redFlagType === undefined || action.redFlagType === null) {
			console.error('saveRedFlagType received a null');
			throw new Error('saveRedFlagType received a null');
		}

		if (!(action.redFlagType instanceof RedFlagType)) {
			action.redFlagType = new RedFlagType(action.redFlagType);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.redFlagType.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.redFlagType);
			const addResult = yield call(idb.add, 'redFlagType', action.redFlagType);
			if (addResult.isErr()) {
				console.error(`⮂ saveRedFlagType: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertRedFlagTypeSave(action.redFlagType, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.REDFLAGTYPE_SAVE_FAILURE, redFlagType: action.redFlagType, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.REDFLAGTYPE_SAVE, redFlagType: action.redFlagType, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertRedFlagTypeSave(action.redFlagType, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.REDFLAGTYPE_SAVE_FAILURE, redFlagType: action.redFlagType, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {RedFlagTypeAction} action
 * @param {RedFlagType[]} action.redFlagTypes
 */
export function* saveManyRedFlagTypes(action: RedFlagTypeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ REDFLAGTYPE_SAVE_MANY saga', action.redFlagTypes.map(redFlagType => redFlagType.id).join(','));

        if (!Array.isArray(action.redFlagTypes)) {
            console.error('saveManyRedFlagTypes expected an array, received', action.redFlagTypes);
            throw new Error('saveManyRedFlagTypes expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveRedFlagType), under these queue keys
		const dispatchedAddedOns: number[] = action.redFlagTypes.map(redFlagType => redFlagType?.addedOn);
		const revertAll = () => action.redFlagTypes.map((redFlagType, i) =>
			put(Actions.revertRedFlagTypeSave(redFlagType, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: RedFlagType[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.redFlagTypes.length; i++) {
				const redFlagType = action.redFlagTypes[i];
				redFlagType.persisted = false;
				ensureOperationId(redFlagType);
				toSave.push(redFlagType);
			}

			const addResult = yield call(idb.add, 'redFlagType', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyRedFlagTypes: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.REDFLAGTYPE_SAVE_MANY_FAILURE, redFlagTypes: action.redFlagTypes, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.REDFLAGTYPE_SAVE_MANY, redFlagTypes: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.REDFLAGTYPE_SAVE_MANY_FAILURE, redFlagTypes: action.redFlagTypes, status: 500, err});

			console.error(`Error saving ${action.redFlagTypes.length} redFlagTypes to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {RedFlagTypeAction} action
 */
export function* removeRedFlagType(action: RedFlagTypeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.redFlagType === undefined || action.redFlagType === null || !(action.redFlagType instanceof RedFlagType)) {
			console.error('removeRedFlagType expected type RedFlagType, received', action.redFlagType);
			reportQueued(false);
			throw new Error('removeRedFlagType expected type RedFlagType');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.redFlagType.status = 'D';
		action.redFlagType.addedOn = newHRTimestamp();
		action.redFlagType.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.redFlagType);
		const addResult = yield call(idb.add, 'redFlagType', action.redFlagType);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeRedFlagType: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.REDFLAGTYPE_REMOVE_FAILURE, redFlagType: action.redFlagType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAGTYPE_REMOVE, redFlagType: action.redFlagType});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REDFLAGTYPE_REMOVE_FAILURE, redFlagType: action.redFlagType, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this RedFlagType.
	*
	* @param {RedFlagTypeAction} action
	*/
export function* restoreRedFlagType(action: RedFlagTypeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreRedFlagType expected getCurrentUserId() to return a value');
        return;
    }

	if (action.redFlagType === undefined || action.redFlagType === null) {
        console.error('restoreRedFlagType expected a RedFlagType, received null');
        throw new Error('restoreRedFlagType expected a RedFlagType, received null');
    }
    else if (!(action.redFlagType instanceof RedFlagType)) action.redFlagType = new RedFlagType(action.redFlagType); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.redFlagType.status = 'U';
		action.redFlagType.addedOn = newHRTimestamp();
		action.redFlagType.addedBy = userId;
		action.redFlagType.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.redFlagType);
		const addResult = yield call(idb.add, 'redFlagType', action.redFlagType);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreRedFlagType: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REDFLAGTYPE_RESTORE_FAILURE, redFlagType: action.redFlagType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAGTYPE_RESTORE, redFlagType: action.redFlagType});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REDFLAGTYPE_RESTORE_FAILURE, redFlagType: action.redFlagType, status: 500, err});

		console.error('Error restoring from database', err);
	}
}